import { useEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SpotlightTour, type TourStep } from './SpotlightTour';
import { PAGE_TOURS } from '@/config/tours';
import { isTourSeen, markTourSeen as markTourSeenLocal } from '@/utils/tourStorage';
import { useAuthStore } from '@/stores';
import { authApi } from '@/features/auth';
import type { TourSeen } from '@/types/auth';

// 路由 → 後端 tourSeen 欄位 key
const TOUR_KEY: Record<string, keyof TourSeen> = {
  '/ai-chat': 'aiChat',
  '/social-analysis': 'socialAnalysis',
  '/ai-publish': 'aiPublish',
};

// 進頁後延遲一拍再啟動，等 lazy chunk 與頁面內容渲染完成
const START_DELAY = 600;
// /ai-publish 有發文免責 Dialog，導覽須等使用者「同意」後才開始（避免兩個彈窗衝突）
const DISCLAIMER_GATED_PATH = '/ai-publish';
const DISCLAIMER_DATE_KEY = 'intellio-publish-disclaimer-date';
const DISCLAIMER_ACCEPTED_EVENT = 'intellio:publish-disclaimer-accepted';
const isDisclaimerAcceptedToday = () =>
  localStorage.getItem(DISCLAIMER_DATE_KEY) === new Date().toISOString().slice(0, 10);

/**
 * 監聽路由，當進入有設定導覽的功能頁且該頁首次造訪時，
 * 自動啟動該頁的 spotlight 導覽。掛在 MainLayout 內。
 */
export function TourController() {
  const location = useLocation();
  const { t } = useTranslation('tutorial');
  const theme = useTheme();
  // 導覽僅限桌面版：手機版元素定位有問題（側欄收合、元素隱藏），暫不啟用
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const user = useAuthStore((state) => state.user);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const pathname = location.pathname;

  useEffect(() => {
    const config = PAGE_TOURS[pathname];
    // 「已看過」以後端 tourSeen 為主、localStorage 為輔（後端 schema 尚未支援時防重複）
    const tourKey = TOUR_KEY[pathname];
    const seenBackend = tourKey ? user?.tourSeen?.[tourKey] === true : false;
    // 僅桌面版、且該頁尚未看過才觸發
    if (!config || !isDesktop || seenBackend || isTourSeen(pathname)) {
      setActiveKey(null);
      return;
    }

    let timer: number | undefined;
    const start = () => {
      timer = window.setTimeout(() => {
        // 通知頁面內容：導覽即將啟動，請切回導覽所需的預設視圖（避免錨點被 tab 切換隱藏）
        window.dispatchEvent(
          new CustomEvent('intellio:tour-start', { detail: { path: pathname } })
        );
        setActiveKey(pathname);
      }, START_DELAY);
    };

    // /ai-publish：尚未同意免責 → 等「同意」事件後才啟動導覽
    if (pathname === DISCLAIMER_GATED_PATH && !isDisclaimerAcceptedToday()) {
      const onAccepted = () => start();
      window.addEventListener(DISCLAIMER_ACCEPTED_EVENT, onAccepted, { once: true });
      return () => {
        window.removeEventListener(DISCLAIMER_ACCEPTED_EVENT, onAccepted);
        if (timer) window.clearTimeout(timer);
      };
    }

    start();
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [pathname, isDesktop, user?.tourSeen]);

  if (!activeKey) return null;
  const config = PAGE_TOURS[activeKey];
  if (!config) return null;

  const steps: TourStep[] = config.map((s) => ({
    target: s.target,
    title: t(s.titleKey),
    text: t(s.textKey),
  }));

  const handleFinish = () => {
    markTourSeenLocal(activeKey); // localStorage 即時生效（fallback）
    const tourKey = TOUR_KEY[activeKey];
    if (tourKey) {
      const store = useAuthStore.getState();
      const next: TourSeen = { ...(store.user?.tourSeen ?? {}), [tourKey]: true };
      if (store.user) store.setUser({ ...store.user, tourSeen: next });
      authApi.markTourSeen(next).catch(() => {}); // 寫後端，跨裝置持久化
    }
    setActiveKey(null);
  };

  return (
    <SpotlightTour
      steps={steps}
      open
      onFinish={handleFinish}
      labels={{
        next: t('controls.next'),
        prev: t('controls.prev'),
        done: t('controls.done'),
        skip: t('controls.skip'),
      }}
    />
  );
}
